import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';

export interface SearchResult {
  id: string;
  content_type: 'blog' | 'project' | 'webtool';
  content_id: string;
  title: string;
  slug: string;
  excerpt: string | null;
  content_searchable: string | null;
  category: string;
  tags: string[];
  thumbnail: string | null;
  thumbnail_width?: number | null;
  thumbnail_height?: number | null;
  created_at: string;
  updated_at: string;
}

export interface SearchResponse {
  results: SearchResult[];
  query: string;
  total: number;
  contentType: string;
  offset: number;
  limit: number;
}

export const useSearch = (query: string, contentType: string = 'all') => {
  return useQuery({
    queryKey: ['search', query, contentType],
    queryFn: async (): Promise<SearchResult[]> => {
      if (query.length < 2) {
        return [];
      }

      const params = new URLSearchParams({
        q: query,
        type: contentType,
        limit: '20',
        offset: '0'
      });

      const { data, error } = await supabase.functions.invoke('search', {
        body: Object.fromEntries(params)
      });

      if (error) {
        console.error('Search error:', error);
        throw error;
      }

      return data?.results || [];
    },
    enabled: query.length >= 2,
    staleTime: 30000, // 30 seconds
    gcTime: 5 * 60 * 1000, // 5 minutes
  });
};

// Hook for recent searches (stored in localStorage)
const RECENT_SEARCHES_KEY = 'recent_searches';
const MAX_RECENT_SEARCHES = 5;

export const useRecentSearches = () => {
  const getRecentSearches = (): string[] => {
    try {
      const stored = localStorage.getItem(RECENT_SEARCHES_KEY);
      return stored ? JSON.parse(stored) : [];
    } catch {
      return [];
    }
  };

  const addRecentSearch = (query: string) => {
    if (!query.trim() || query.length < 2) return;

    const recent = getRecentSearches();
    const filtered = recent.filter(q => q.toLowerCase() !== query.toLowerCase());
    const updated = [query, ...filtered].slice(0, MAX_RECENT_SEARCHES);

    try {
      localStorage.setItem(RECENT_SEARCHES_KEY, JSON.stringify(updated));
    } catch (error) {
      console.error('Error saving recent search:', error);
    }
  };

  const clearRecentSearches = () => {
    try {
      localStorage.removeItem(RECENT_SEARCHES_KEY);
    } catch (error) {
      console.error('Error clearing recent searches:', error);
    }
  };

  return {
    recentSearches: getRecentSearches(),
    addRecentSearch,
    clearRecentSearches
  };
};
